// components/Details/Details.js - Browse mode: View everything, login only for orders
import React, { useState, useEffect } from 'react';
import { useCart } from '../../contexts/CartContext';
import productAPI from '../../services/productApi';
import './Details.css';

const Details = ({ 
  itemId, 
  itemType, 
  setCurrentPage, 
  onBack, 
  onRelatedItemClick,
  isLoggedIn = false,
  onLoginRequired
}) => {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [relatedItems, setRelatedItems] = useState([]);
  
  // Contact information
  const EMAIL = 'Info@transcontinentalnetwork.com';
  const PHONE_NUMBER = '347.668.2160';
  const PHONE_NUMERIC = '13476682160';
  const ADDRESS = '160-04 Cross Bay Blvd. Howard Beach, NY 11414';
  
  // Cart integration (only for products and logged in users)
  const { addToCart, getItemQuantity, isInCart, formatPrice } = useCart();
  const [addingToCart, setAddingToCart] = useState(false);
  const [addToCartMessage, setAddToCartMessage] = useState('');
  
  // Quote modal state (for services)
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  // Load item details - PUBLIC ACCESS (no login required)
  useEffect(() => {
    if (itemId && itemType) {
      loadItemDetails();
    }
  }, [itemId, itemType]);

  // Clear messages after delay
  useEffect(() => {
    if (addToCartMessage) {
      const timer = setTimeout(() => {
        setAddToCartMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [addToCartMessage]);

  const loadItemDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      // API calls work for both logged-in and guest users
      if (itemType === 'product') {
        response = await productAPI.getProduct(itemId);
      } else if (itemType === 'service') {
        response = await productAPI.getService(itemId);
      }

      // Handle API response structure: { success: true, data: { product/service: {...} }, message: "..." }
      if (response && response.success) {
        // Extract the actual item data from the nested structure
        const apiData = response.data || {};
        const itemData = apiData.product || apiData.service || null;
        
        if (!itemData) {
          throw new Error('Item data not found in API response');
        }
        
        const transformedItem = transformItemData(itemData);
        setItem(transformedItem);
        
        // Load related items based on category
        const categoryId = itemData.category_id || itemData.category?.id;
        if (categoryId) {
          loadRelatedItems(categoryId);
        }
      } else {
        throw new Error(response?.message || 'Failed to load item');
      }
    } catch (error) {
      console.error('Error loading item details:', error);
      const errorInfo = productAPI.handleAPIError(error);
      setError(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedItems = async (categoryId) => {
    try {
      const params = {
        category_id: categoryId,
        per_page: 4,
        page: 1
      };

      let response;
      // API calls work for both logged-in and guest users
      if (itemType === 'product') {
        response = await productAPI.getProducts(params);
      } else {
        response = await productAPI.getServices(params);
      }

      // Handle API response structure
      if (response && response.success) {
        const apiData = response.data || {};
        const allItems = apiData.products || apiData.services || [];
        
        // Filter out current item and get first 3
        const filtered = allItems
          .filter(i => i.id.toString() !== itemId.toString())
          .slice(0, 3)
          .map(transformItemData);
        
        setRelatedItems(filtered);
      }
    } catch (error) {
      console.error('Error loading related items:', error);
      setRelatedItems([]);
    }
  };

  const transformItemData = (data) => {
    // Handle images array - they may come as objects or strings
    let imageUrls = [];
    if (Array.isArray(data.images)) {
      imageUrls = data.images
        .map(img => {
          if (typeof img === 'string') return img;
          if (img.url) return img.url;
          if (img.image_url) return img.image_url;
          return null;
        })
        .filter(Boolean);
    }

    // Use primary_image if available and no images in array
    if (imageUrls.length === 0 && data.primary_image) {
      imageUrls = [data.primary_image];
    }

    return {
      id: data.id,
      title: data.name || data.title,
      description: data.description || '',
      longDescription: data.long_description || data.details || data.description || '',
      category: data.category?.name || 'Professional Equipment',
      images: imageUrls,
      price: parseFloat(data.price) || 0,
      salePrice: data.sale_price ? parseFloat(data.sale_price) : null,
      isOnSale: data.is_on_sale === true || data.is_on_sale === 1,
      inStock: data.in_stock !== false && (data.stock_quantity === undefined || data.stock_quantity > 0),
      stockQuantity: parseInt(data.stock_quantity) || null,
      features: Array.isArray(data.features) ? data.features : [],
      specifications: Array.isArray(data.specifications) ? data.specifications : [],
      tags: Array.isArray(data.tags) ? data.tags : [],
      warranty: data.warranty || '1 Year Manufacturer Warranty',
      brand: data.brand || 'Professional Grade',
      model: data.model || '',
      sku: data.sku || `${itemType.toUpperCase()}-${data.id}`,
      type: itemType,
      categoryId: data.category_id
    };
  };

  const calculateDiscount = () => {
    if (item?.isOnSale && item?.price && item?.salePrice) {
      const discount = ((item.price - item.salePrice) / item.price * 100);
      return Math.round(discount);
    }
    return 0;
  };

  const handleQuantityChange = (increment) => {
    const newQuantity = Math.max(1, quantity + increment);
    const maxQuantity = item?.stockQuantity || 999;
    setQuantity(Math.min(newQuantity, maxQuantity));
  };

  const handleAddToCart = async () => {
    // Check if user is logged in - REQUIRED FOR ORDERING
    if (!isLoggedIn) {
      alert('Please login or signup to add items to cart');
      if (onLoginRequired) {
        onLoginRequired();
      } else {
        setCurrentPage('login');
      }
      return;
    }

    if (!item) return;

    setAddingToCart(true);
    setAddToCartMessage('');

    try {
      // Check if item is in stock
      if (itemType === 'product' && !item.inStock) {
        throw new Error('Product is out of stock');
      }

      // Check quantity limits
      if (itemType === 'product' && item.stockQuantity && quantity > item.stockQuantity) {
        throw new Error(`Only ${item.stockQuantity} items available`);
      }

      // Add to cart
      const success = addToCart(item, quantity);
      
      if (success) {
        setAddToCartMessage(`Added ${quantity} x ${item.title} to cart!`);
        setQuantity(1);
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      setAddToCartMessage(error.message || 'Failed to add item to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleGetQuote = () => {
    // Check if user is logged in - REQUIRED FOR QUOTE REQUEST
    if (!isLoggedIn) {
      alert('Please login or signup to request a quote');
      if (onLoginRequired) {
        onLoginRequired();
      } else {
        setCurrentPage('login');
      }
      return;
    }

    setShowQuoteModal(true);
  };

  const handleCloseQuoteModal = () => {
    setShowQuoteModal(false);
  };

  const handleEmailContact = () => {
    const subject = encodeURIComponent(`Quote Request: ${item.title}`);
    const body = encodeURIComponent(
      `Hi,\n\nI'm interested in getting a quote for: ${item.title}\n\nService ID: ${item.sku}\nPrice: ${formatPrice(item.price)}\n\nPlease provide more details and schedule an appointment.\n\nThank you!`
    );
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent(EMAIL)}&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');
  };

  const handlePhoneContact = () => {
    window.location.href = `tel:+${PHONE_NUMERIC}`;
  };

  const handleRelatedItemClick = (relatedItemId) => {
    if (onRelatedItemClick && typeof onRelatedItemClick === 'function') {
      onRelatedItemClick(relatedItemId, itemType);
    }
  };

  const handleViewCart = () => {
    // Check if user is logged in
    if (!isLoggedIn) {
      alert('Please login or signup to view cart');
      if (onLoginRequired) {
        onLoginRequired();
      } else {
        setCurrentPage('login');
      }
      return;
    }

    setCurrentPage('cart');
  };

  const handleLoginClick = () => {
    if (onLoginRequired) {
      onLoginRequired();
    } else {
      setCurrentPage('login');
    }
  };

  const currentCartQuantity = isLoggedIn && item ? getItemQuantity(item.id, item.type) : 0;
  const totalInCart = currentCartQuantity + quantity;
  const canAddMore = !item?.stockQuantity || totalInCart <= item.stockQuantity;

  if (loading) {
    return (
      <div className="details-page">
        <div className="container">
          <div className="loading-spinner" style={{ textAlign: 'center', padding: '4rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
            <p>Loading details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="details-page">
        <div className="container">
          <div className="error-message" style={{ textAlign: 'center', padding: '4rem' }}>
            <h2>Error Loading Details</h2>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={loadItemDetails}>
              Try Again
            </button>
            <button className="btn btn-secondary" onClick={onBack} style={{ marginLeft: '1rem' }}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="details-page">
        <div className="container">
          <div className="not-found" style={{ textAlign: 'center', padding: '4rem' }}>
            <h2>Item Not Found</h2>
            <p>The requested {itemType} could not be found.</p>
            <button className="btn btn-primary" onClick={onBack}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="details-page">
      {/* Login Required Banner - Only show if not logged in */}
      {!isLoggedIn && (
        <div className="login-banner">
          <div className="container">
            <div className="banner-content">
              <span className="banner-icon">🔒</span>
              <span className="banner-text">
                Want to purchase? <strong>Login or Signup</strong> to add items to cart and place orders
              </span>
              <button 
                className="btn btn-sm btn-primary"
                onClick={handleLoginClick}
              >
                Login / Signup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Cart Message */}
      {itemType === 'product' && addToCartMessage && isLoggedIn && (
        <div className={`cart-message ${addToCartMessage.includes('Added') ? 'success' : 'error'}`}>
          <div className="container">
            <div className="message-content">
              <span>{addToCartMessage}</span>
              {addToCartMessage.includes('Added') && (
                <button 
                  className="btn btn-sm btn-secondary view-cart-btn"
                  onClick={handleViewCart}
                >
                  View Cart
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quote Modal */}
      {itemType === 'service' && showQuoteModal && (
        <div className="modal-overlay" onClick={handleCloseQuoteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseQuoteModal}>×</button>
            <div className="quote-modal">
              <h2>Request a Quote</h2>
              <div className="service-info">
                <h3>{item.title}</h3>
                <p className="service-price">Starting at: {formatPrice(item.price)}</p>
              </div>
              
              <div className="contact-options">
                <p className="contact-intro">
                  Get in touch with us to schedule your appointment and receive a detailed quote:
                </p>
                
                <div className="contact-buttons">
                  <button 
                    className="btn btn-primary btn-large contact-btn email-btn"
                    onClick={handleEmailContact}
                  >
                    <span className="btn-icon">✉️</span>
                    Email
                  </button>
                  
                  <button 
                    className="btn btn-secondary btn-large contact-btn phone-btn"
                    onClick={handlePhoneContact}
                  >
                    <span className="btn-icon">📞</span>
                    Call Us Now
                  </button>
                </div>

                <div className="contact-details">
                  <p><strong>Email:</strong> <a href={`https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent(EMAIL)}`} target="_blank" rel="noopener noreferrer">{EMAIL}</a></p>
                  <p><strong>Phone:</strong> <a href={`tel:+${PHONE_NUMERIC}`}>{PHONE_NUMBER}</a></p>
                  <p><strong>Address:</strong> {ADDRESS}</p>
                  <p><strong>Business Hours:</strong> Mon-Sat, 9:00 AM - 6:00 PM</p>
                  <p className="contact-note">
                    Our team will respond within 24 hours to discuss your requirements and schedule an appointment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <section className="breadcrumb">
        <div className="container">
          <nav className="breadcrumb-nav">
            <button onClick={() => setCurrentPage('home')} className="breadcrumb-link">Home</button>
            <span className="breadcrumb-separator">/</span>
            <button onClick={() => setCurrentPage(itemType === 'product' ? 'products' : 'services')} className="breadcrumb-link">
              {itemType === 'product' ? 'Products' : 'Services'}
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{item.title}</span>
          </nav>
        </div>
      </section>

      {/* Main Content */}
      <section className="item-details section">
        <div className="container">
          <div className="details-grid">
            {/* Image Gallery */}
            <div className="image-gallery">
              <div className="main-image">
                {item.images && item.images.length > 0 ? (
                  <img 
                    src={item.images[selectedImage]} 
                    alt={item.title}
                  />
                ) : (
                  <div className="no-image-placeholder" style={{ 
                    width: '100%', 
                    height: '400px', 
                    backgroundColor: '#f5f5f5', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#999'
                  }}>
                    No Image Available
                  </div>
                )}
                {item.isOnSale && (
                  <div className="discount-badge">-{calculateDiscount()}%</div>
                )}
              </div>
              
              {item.images && item.images.length > 1 && (
                <div className="image-thumbnails">
                  {item.images.map((image, index) => (
                    <button
                      key={index}
                      className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                    >
                      <img src={image} alt={`${item.title} ${index + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Item Information */}
            <div className="item-info">
              <div className="item-header">
                <span className="item-category">{item.category}</span>
                <h1 className="item-title">{item.title}</h1>

                {/* Price - ALWAYS VISIBLE (no login required) */}
                <div className="price-section">
                  {item.isOnSale && item.salePrice ? (
                    <div className="sale-prices">
                      <span className="original-price">{formatPrice(item.price)}</span>
                      <span className="sale-price">{formatPrice(item.salePrice)}</span>
                      <span className="savings">Save {calculateDiscount()}%</span>
                    </div>
                  ) : (
                    <span className="current-price">
                      {itemType === 'service' ? 'Starting at ' : ''}{formatPrice(item.price)}
                    </span>
                  )}
                </div>

                {/* Stock Status - ALWAYS VISIBLE (no login required) */}
                {itemType === 'product' && (
                  <div className="stock-status">
                    {item.inStock ? (
                      <span className="in-stock">✓ In Stock</span>
                    ) : (
                      <span className="out-of-stock">⚠️ Currently Unavailable</span>
                    )}
                    {item.stockQuantity && (
                      <span className="stock-quantity"> - {item.stockQuantity} available</span>
                    )}
                  </div>
                )}

                {/* Availability Status - ALWAYS VISIBLE (no login required) */}
                {itemType === 'service' && (
                  <div className="stock-status">
                    <span className="in-stock">✓ Available for Booking</span>
                  </div>
                )}

                {/* Cart Status - ONLY for logged in users */}
                {itemType === 'product' && isLoggedIn && currentCartQuantity > 0 && (
                  <div className="cart-status">
                    <span className="in-cart-badge">
                      {currentCartQuantity} in cart
                    </span>
                  </div>
                )}
              </div>

              {/* Description - ALWAYS VISIBLE (no login required) */}
              <div className="item-description">
                <p>{item.description}</p>
                {item.longDescription && item.longDescription !== item.description && (
                  <div className="long-description">
                    <p>{item.longDescription}</p>
                  </div>
                )}
              </div>

              {/* Features - ALWAYS VISIBLE (no login required) */}
              {item.features && item.features.length > 0 && (
                <div className="features-section">
                  <h3>Key Features</h3>
                  <ul className="features-list">
                    {item.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions - PRODUCT (Login required for cart actions) */}
              {itemType === 'product' && (
                <div className="item-actions">
                  {isLoggedIn ? (
                    <>
                      <div className="quantity-selector">
                        <label htmlFor="quantity">Quantity:</label>
                        <div className="quantity-controls">
                          <button 
                            onClick={() => handleQuantityChange(-1)} 
                            disabled={quantity <= 1}
                          >
                            -
                          </button>
                          <span className="quantity-display">{quantity}</span>
                          <button 
                            onClick={() => handleQuantityChange(1)}
                            disabled={!canAddMore}
                          >
                            +
                          </button>
                        </div>
                        {item.stockQuantity && (
                          <small className="quantity-note">
                            {totalInCart > item.stockQuantity 
                              ? `Only ${item.stockQuantity} available` 
                              : `${item.stockQuantity} available`
                            }
                          </small>
                        )}
                      </div>

                      <div className="action-buttons">
                        <button 
                          className="btn btn-primary btn-large add-to-cart-btn"
                          onClick={handleAddToCart}
                          disabled={!item.inStock || addingToCart || !canAddMore}
                        >
                          {addingToCart ? 'Adding...' : 
                           !item.inStock ? 'Out of Stock' : 
                           !canAddMore ? 'Max Quantity Reached' :
                           `Add to Cart${quantity > 1 ? ` (${quantity})` : ''}`}
                        </button>

                        {currentCartQuantity > 0 && (
                          <button 
                            className="btn btn-secondary view-cart-btn"
                            onClick={handleViewCart}
                          >
                            View Cart ({currentCartQuantity})
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="login-required-section">
                      <div className="login-required-message">
                        <span className="lock-icon">🔒</span>
                        <p>Login required to add items to cart and place orders</p>
                      </div>
                      <button 
                        className="btn btn-primary btn-large"
                        onClick={handleLoginClick}
                      >
                        Login / Signup to Purchase
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Actions - SERVICE (Login required for quote) */}
              {itemType === 'service' && (
                <div className="item-actions">
                  <div className="action-buttons service-actions">
                    {isLoggedIn ? (
                      <>
                        <button 
                          className="btn btn-primary btn-large"
                          onClick={handleGetQuote}
                        >
                          Get a Free Quote
                        </button>
                        <p className="service-note">
                          Contact us for a personalized quote and to schedule your appointment
                        </p>
                      </>
                    ) : (
                      <div className="login-required-section">
                        <div className="login-required-message">
                          <span className="lock-icon">🔒</span>
                          <p>Login required to request quotes and book services</p>
                        </div>
                        <button 
                          className="btn btn-primary btn-large"
                          onClick={handleLoginClick}
                        >
                          Login / Signup to Request Quote
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Info - ALWAYS VISIBLE (no login required) */}
              <div className="additional-info">
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Brand:</span>
                    <span className="info-value">{item.brand}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">SKU:</span>
                    <span className="info-value">{item.sku}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">
                      {itemType === 'product' ? 'Warranty:' : 'Service Guarantee:'}
                    </span>
                    <span className="info-value">{item.warranty}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications - ALWAYS VISIBLE (no login required) */}
      {item.specifications && item.specifications.length > 0 && (
        <section className="specifications section">
          <div className="container">
            <h2>Technical Specifications</h2>
            <div className="specs-grid">
              {item.specifications.map((spec, index) => (
                <div key={index} className="spec-item">
                  <span className="spec-label">{spec.split(':')[0]}:</span>
                  <span className="spec-value">{spec.split(':').slice(1).join(':')}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Items - ALWAYS VISIBLE (no login required) */}
      {relatedItems && relatedItems.length > 0 && (
        <section className="related-items section">
          <div className="container">
            <h2>Related {itemType === 'product' ? 'Products' : 'Services'}</h2>
            <div className="grid-3">
              {relatedItems.map(relatedItem => (
                <div key={relatedItem.id} className="related-card">
                  <div className="related-image">
                    {relatedItem.images && relatedItem.images.length > 0 ? (
                      <img src={relatedItem.images[0]} alt={relatedItem.title} />
                    ) : (
                      <div className="no-image-placeholder" style={{ 
                        width: '100%', 
                        height: '200px', 
                        backgroundColor: '#f5f5f5', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '0.9rem'
                      }}>
                        No Image
                      </div>
                    )}
                  </div>
                  <div className="related-info">
                    <h3>{relatedItem.title}</h3>
                    <p className="related-price">{formatPrice(relatedItem.price)}</p>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => handleRelatedItemClick(relatedItem.id)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Details;